/* eslint-disable */
"use client";

import React, { useState } from 'react';
import Breadcrumb from './ui/Breadcrumb';
import { useTranslations } from 'next-intl';
import { HelpOutlineIcon, AssignmentIcon, CardGiftcardIcon, SecurityOutlinedIcon, HeadsetMicIcon, ExpandMoreIcon } from './icons';
import { PreferredSourceBlock } from './PreferredSource/PreferredSourceBlock';
import { PreferredSourcePlacement } from '../types/preferredSource';
import styles from './StaticPage.module.css';

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQCategory {
    title: string;
    icon: React.ReactNode;
    items: FAQItem[];
}

// FAQ Page Component
const FAQ: React.FC = () => {
    const t = useTranslations('faq');
    const tCommon = useTranslations('common');
    // Define breadcrumb items
    const breadcrumbItems = [
        { label: tCommon('breadcrumb.home'), path: '/' },
        { label: tCommon('breadcrumb.faq'), path: '/faq' }
    ];

    const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
    const toggleItem = (key: string) => setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));

    // Define FAQ categories and items
    const faqCategories: FAQCategory[] = [
        {
            title: t('categories.aboutAceb.title'),
            icon: <HelpOutlineIcon />,
            items: [
                {
                    question: t('categories.aboutAceb.items.whatIsAceb.question'),
                    answer: t('categories.aboutAceb.items.whatIsAceb.answer')
                },
                {
                    question: t('categories.aboutAceb.items.isAcebLegit.question'),
                    answer: t('categories.aboutAceb.items.isAcebLegit.answer')
                }
            ]
        },
        {
            title: t('categories.payments.title'),
            icon: <AssignmentIcon />,
            items: [
                {
                    question: t('categories.payments.items.whichCrypto.question'),
                    answer: t('categories.payments.items.whichCrypto.answer')
                },
                {
                    question: t('categories.payments.items.creditCard.question'),
                    answer: t('categories.payments.items.creditCard.answer')
                },
                {
                    question: t('categories.payments.items.paymentSpeed.question'),
                    answer: t('categories.payments.items.paymentSpeed.answer')
                }
            ]
        },
        {
            title: t('categories.giftCards.title'),
            icon: <CardGiftcardIcon />,
            items: [
                {
                    question: t('categories.giftCards.items.howReceive.question'),
                    answer: t('categories.giftCards.items.howReceive.answer')
                },
                {
                    question: t('categories.giftCards.items.deliveryTime.question'),
                    answer: t('categories.giftCards.items.deliveryTime.answer')
                },
                {
                    question: t('categories.giftCards.items.countries.question'),
                    answer: t('categories.giftCards.items.countries.answer')
                },
                {
                    question: t('categories.giftCards.items.expiration.question'),
                    answer: t('categories.giftCards.items.expiration.answer')
                }
            ]
        },
        {
            title: t('categories.orders.title'),
            icon: <AssignmentIcon />,
            items: [
                {
                    question: t('categories.orders.items.refund.question'),
                    answer: t('categories.orders.items.refund.answer')
                },
                {
                    question: t('categories.orders.items.didntReceive.question'),
                    answer: t('categories.orders.items.didntReceive.answer')
                },
                {
                    question: t('categories.orders.items.codeDoesntWork.question'),
                    answer: t('categories.orders.items.codeDoesntWork.answer')
                }
            ]
        },
        {
            title: t('categories.account.title'),
            icon: <SecurityOutlinedIcon />,
            items: [
                {
                    question: t('categories.account.items.needAccount.question'),
                    answer: t('categories.account.items.needAccount.answer')
                },
                {
                    question: t('categories.account.items.dataSafe.question'),
                    answer: t('categories.account.items.dataSafe.answer')
                }
            ]
        },
        {
            title: t('categories.support.title'),
            icon: <HeadsetMicIcon />,
            items: [
                {
                    question: t('categories.support.items.howContact.question'),
                    answer: t('categories.support.items.howContact.answer')
                },
                {
                    question: t('categories.support.items.languages.question'),
                    answer: t('categories.support.items.languages.answer')
                }
            ]
        }
    ];

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.breadcrumbBar}>
                <div className={styles.container}>
                    <Breadcrumb items={breadcrumbItems} />
                </div>
            </div>

            <div className={styles.heroSection}>
                <div className={styles.heroInner}>
                    <h1 className={styles.heroTitle}>{t('hero.title')}</h1>
                    <p className={styles.heroSubtitle}>{t('hero.subtitle')}</p>
                </div>
            </div>

            <div className={styles.container}>
                <div className={styles.mainGrid}>
                    <div className={styles.contentColumn}>
                        <h2 className={styles.sectionTitle}>{t('sectionTitle')}</h2>

                        {faqCategories.map((category, categoryIndex) => (
                            <div key={`category-${categoryIndex}`} style={{ marginBottom: 40 }}>
                                <div className={styles.categoryHeader}>
                                    <div className={styles.categoryIconBox}>
                                        {category.icon}
                                    </div>
                                    <h3 className={styles.categoryTitle}>{category.title}</h3>
                                </div>

                                {category.items.map((item, itemIndex) => {
                                    const key = `faq-${categoryIndex}-${itemIndex}`;
                                    const isOpen = !!openItems[key];
                                    return (
                                        <div key={key} className={styles.accordion}>
                                            <div
                                                className={styles.accordionSummary}
                                                onClick={() => toggleItem(key)}
                                                role="button"
                                                aria-expanded={isOpen}
                                                aria-controls={`panel-${categoryIndex}-${itemIndex}-content`}
                                            >
                                                <span className="questionText" style={{ flex: 1, fontWeight: 600, fontSize: 16 }}>
                                                    {item.question}
                                                </span>
                                                <span className={`${styles.accordionExpandIcon} ${isOpen ? styles.expanded : ''}`}>
                                                    <ExpandMoreIcon />
                                                </span>
                                            </div>
                                            <div
                                                id={`panel-${categoryIndex}-${itemIndex}-content`}
                                                className={`${styles.accordionDetails} ${isOpen ? styles.open : ''}`}
                                            >
                                                <p className={styles.accordionAnswer}>{item.answer}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}

                        <hr className={styles.divider} />
                        <PreferredSourceBlock placement={PreferredSourcePlacement.FAQ} />
                    </div>

                    <div className={styles.sidebarColumn}>
                        <div className={styles.sidebarCard}>
                            <h3 className={styles.sidebarCardTitle}>{t('sidebar.title')}</h3>
                            <p className={styles.sidebarCardText}>{t('sidebar.description')}</p>

                            <hr className={styles.divider} />

                            <div>
                                <p style={{ fontWeight: 600, marginBottom: 16 }}>{t('sidebar.contactUs')}</p>
                                <div className={styles.sidebarContactRow}>
                                    <HeadsetMicIcon />
                                    <span>{t('sidebar.email')}</span>
                                </div>
                                <p style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)', marginTop: 16 }}>
                                    {t('sidebar.responseTime')}
                                </p>
                            </div>

                            <div className={styles.sidebarHelpBox}>
                                <h4>{t('sidebar.helpFaster.title')}</h4>
                                <p>{t('sidebar.helpFaster.description')}</p>
                                <ul>
                                    <li>{t('sidebar.helpFaster.point1')}</li>
                                    <li>{t('sidebar.helpFaster.point2')}</li>
                                    <li>{t('sidebar.helpFaster.point3')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FAQ;